import React from 'react';

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ 
  icon, 
  title, 
  description, 
  action 
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="text-neutral-400 mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
      <p className="text-sm text-neutral-600 max-w-sm mb-6">{description}</p>
      {action && <div>{action}</div>}
    </div>
  );
};

// Loading Skeleton
interface SkeletonProps {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => {
  return (
    <div className={`animate-pulse bg-neutral-200 rounded ${className}`} />
  );
};

// Card Skeleton
export const CardSkeleton = () => {
  return (
    <div className="bg-neutral-50 rounded-xl p-6 ring-1 ring-neutral-100">
      <Skeleton className="h-4 w-1/4 mb-4" />
      <Skeleton className="h-6 w-1/2 mb-2" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
};

// Table Skeleton
export const TableSkeleton = ({ rows = 5 }: { rows?: number }) => {
  return (
    <div className="overflow-hidden ring-1 ring-neutral-200 rounded-lg">
      <div className="bg-neutral-50 p-4 border-b border-neutral-200">
        <div className="flex gap-4">
          <Skeleton className="h-3 w-1/4" />
          <Skeleton className="h-3 w-1/4" />
          <Skeleton className="h-3 w-1/4" />
          <Skeleton className="h-3 w-1/4" />
        </div>
      </div>
      <div className="bg-white divide-y divide-neutral-100">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="p-4">
            <div className="flex gap-4">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Dashboard Skeleton — mimics a stat grid + two content panels
export const DashboardSkeleton = () => {
  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-6 animate-pulse">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1.5">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-3.5 w-28" />
        </div>
        <Skeleton className="h-9 w-32 rounded-10" />
      </div>
      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-card rounded-12 border border-ink-100 p-5">
            <Skeleton className="h-3 w-20 mb-3" />
            <Skeleton className="h-7 w-12" />
          </div>
        ))}
      </div>
      {/* Content panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[0, 1].map(i => (
          <div key={i} className="bg-card rounded-12 border border-ink-100 p-5 space-y-3">
            <Skeleton className="h-3 w-24 mb-1" />
            {Array.from({ length: 3 }).map((_, j) => (
              <div key={j} className="flex items-center gap-3">
                <Skeleton className="h-2 w-2 rounded-full flex-none" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-2.5 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
